import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MapPin, X } from "lucide-react";
import api from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const m = (Date.now() - new Date(iso).getTime()) / 60000;
  if (m < 1) return "just now";
  if (m < 60) return `${Math.floor(m)}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
}

export default function NotificationBell() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 64, right: 12, width: 320 });
  const ref = useRef(null);
  const btnRef = useRef(null);
  const navigate = useNavigate();

  const unread = alerts.filter((a) => !a.read).length;

  const load = async () => {
    try { const { data } = await api.get("/alerts"); setAlerts(data); } catch { /* noop */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    if (next && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const width = Math.min(320, window.innerWidth - 24);
      let right = Math.max(12, window.innerWidth - r.right);
      right = Math.min(right, window.innerWidth - width - 12);
      setPos({ top: r.bottom + 8, right, width });
    }
    setOpen(next);
    if (next && unread > 0) {
      await api.post("/alerts/read").catch(() => {});
      setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button ref={btnRef} onClick={toggle} data-testid="notification-bell" className="relative p-2 rounded-lg text-slate-400 hover:text-teal-300 hover:bg-slate-800/60 transition-colors">
        <Bell size={18} />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-teal-400 text-slate-900 text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{ position: "fixed", top: pos.top, right: pos.right, width: pos.width }} className="liquid-glass rounded-xl overflow-hidden z-[70]" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="font-display font-semibold text-slate-100 text-sm">Alerts</span>
            <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-300"><X size={15} /></button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">No alerts yet. New jobs near you will appear here.</div>
            ) : (
              alerts.map((a) => {
                const isJob = a.type !== "candidate_match" && a.type !== "message";
                const heading = a.type === "candidate_match" ? `New candidate: ${a.title}`
                  : a.type === "message" ? `Message from ${a.title}`
                  : `New match: ${a.title}`;
                const dest = a.type === "candidate_match" ? "/talent" : a.type === "message" ? "/applications" : "/radar";
                return (
                  <button key={a.id} data-testid="notification-item"
                    onClick={() => { setOpen(false); navigate(dest); }}
                    className="w-full text-left px-4 py-3 hover:bg-teal-500/5 border-b border-slate-800 last:border-0">
                    <div className="text-sm text-slate-100 font-medium">{heading}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{a.company}</div>
                    <div className="text-xs text-teal-400 mt-1 flex items-center gap-1 font-mono">
                      {isJob && a.distance_km != null ? <><MapPin size={11} /> {a.distance_km}km away · </> : null}{timeAgo(a.created_at)}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
